import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WidgetData} from '../../../../../../../../core/shared/widget/widget-data';
import {FilterCriteria} from '../../../../../../../../core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from '../../../../../../../../core/shared/dto/object/request/object-request-list';
import {MvsCoreService} from '../../../../../../../../core/shared/service/mvs-core.service';
import {buildCwCaseTypeBiproTableWidget} from '../../../../../../widget/cw-case-type-bipro.widget';
import {buildCwCaseTypeExchangeDefinitionTableWidget} from '../../../../../../widget/cw-exchange-definition.widget';
import {buildCwCaseTypeExchangeSkeletonTableWidget} from '../../../../../../widget/cw-exchange-skeleton.widget';
import {buildCwCaseTypeExchangeFieldDefinitionTableWidget} from '../../../../../../widget/cw-exchange-field-definition.widget';
import {
    buildCwCaseTypeExchangeResponseMappingTableWidget
} from '../../../../../../widget/cw-exchange-response-mapping.widget';
import {buildCwCaseTypeExchangeEchoRuleTableWidget} from '../../../../../../widget/cw-exchange-echo-rule.widget';
import {CwBiproFlowEnum} from '../../../../../../model/dto/enum/exchange/cw-bipro-flow.enum';
import {
    CW_ECHO_RULE_FIELD_REFERENCE_HINT
} from '../../../../../../util/cw-echo-rule-dialect.util';
import {
    CW_SCOPE_COLUMN_LABEL,
    CW_SCOPE_CUSTOM_FIELD_ID,
    CW_SCOPE_INHERITANCE_HINT,
    CW_SCOPE_OPEN_CONFIG_LABEL,
    cwOpenPartyVersionConfig,
    cwScopedCreateCallback
} from '../../../../../../widget/cw-carrier-scope.util';
import {cwPersistTabParam, cwReadTabParam} from '../../shared/cw-case-type-widget.util';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Bipro" conditional navigation point of the CwCaseType standard view — only rendered
 *   when the case type is a BiPRO process. Reuses the read-only Exchange-config table builders from
 *   cw/widget/ (no create buttons) and keeps the Austausch-Werkzeuge tools (config-check +
 *   render-preview) reachable. Inactive tabs are not rendered (@if lazy-init).
 *
 *   CW-EXCHANGE-V2: the request half of the configuration is no longer a per-field binding graph —
 *   binding groups, field bindings, structure nodes and product fragments are gone, and with them
 *   their four tabs and the definition→bindings drill-down. What is left is flat and CaseType-scoped:
 *   the operation anchors, the ONE skeleton, the additional (constant/computed) fields, and the new
 *   response-side Antwort-Zuordnungen.
 */
@Component({
    selector: 'cw-case-type-bipro-view',
    templateUrl: './cw-case-type-bipro-view.component.html',
    styleUrls: ['./cw-case-type-bipro-view.component.scss'],
    standalone: false
})
export class CwCaseTypeBiproViewComponent implements OnInit, OnChanges {

    @Input() caseTypeId: number;

    activeTab: string = 'bipro';

    private readonly validTabs = ['bipro', 'exchange', 'skeletons', 'fieldDefinitions', 'responseMappings',
        'echoRules', 'tools'];

    biproWidget: WidgetData;
    exchangeDefinitionWidget: WidgetData;
    skeletonWidget: WidgetData;
    fieldDefinitionWidget: WidgetData;
    responseMappingWidget: WidgetData;
    echoRuleWidget: WidgetData;

    /**
     * #C.1 (cw_sales_enhancements_round_2) — the cc.ExternalPartyVersion this case type is bound to,
     * i.e. the version stream whose Echo-Regeln it INHERITS. {@code null} for a case type with no
     * version binding; the Echo-Regeln tab then shows its own rules only, exactly as before.
     */
    caseTypeExternalPartyVersionId: number | null = null;

    /**
     * Whether {@link caseTypeExternalPartyVersionId} has been resolved yet. The Echo-Regeln widget is
     * built ONLY after this is true, because the union filter has to be part of the very first query —
     * rebuilding the widget afterwards would re-issue the query and throw away the user's table state.
     */
    partyVersionResolved: boolean = false;

    /** German hint shown above the Echo-Regeln table — what an inherited row is and where it is edited. */
    readonly scopeInheritanceHint = CW_SCOPE_INHERITANCE_HINT;

    /** German hint on the mandatory {@code quelle:} dialect prefix of a Feldreferenz (#C.1, spec §3). */
    readonly fieldReferenceHint = CW_ECHO_RULE_FIELD_REFERENCE_HINT;

    /** Field id of the Geltungsbereich badge column — the ng-template must carry exactly this id. */
    readonly scopeCustomFieldId = CW_SCOPE_CUSTOM_FIELD_ID;

    /** German header of that column — ONE spelling, shared with the curated column set. */
    readonly scopeColumnLabel = CW_SCOPE_COLUMN_LABEL;
    /** German label of the button that opens the party-version configuration surface. */
    readonly openPartyVersionConfigLabel = CW_SCOPE_OPEN_CONFIG_LABEL;


    /**
     * The case type's BiPRO flow — resolved best-effort from its CwCaseTypeBipro row and passed to
     * the config-check panel. Defaults to SIMPLE until/unless a row is found (ported from the old
     * CwCaseTypeObjectBaseComponent.loadCaseTypeBiproFlow).
     */
    caseTypeBiproFlow: CwBiproFlowEnum = CwBiproFlowEnum.SIMPLE;

    constructor(
        protected coreService: MvsCoreService,
        private route: ActivatedRoute,
        private router: Router) {
    }

    ngOnInit(): void {
        this.activeTab = cwReadTabParam(this.route, this.validTabs, this.activeTab);
        this.loadCaseTypeBiproFlow();
        this.loadCaseTypeExternalPartyVersion();
        this.buildActiveTab();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.resetWidgets();
            this.loadCaseTypeBiproFlow();
            this.loadCaseTypeExternalPartyVersion();
            this.buildActiveTab();
        }
    }

    onTabChange(tab: string | number): void {
        this.activeTab = String(tab);
        cwPersistTabParam(this.router, this.route, this.activeTab);
        this.buildActiveTab();
    }

    private buildActiveTab(): void {
        switch (this.activeTab) {
            case 'bipro':
                if (!this.biproWidget) {
                    this.biproWidget = buildCwCaseTypeBiproTableWidget(this.caseTypeId);
                }
                break;
            case 'exchange':
                if (!this.exchangeDefinitionWidget) {
                    this.exchangeDefinitionWidget = buildCwCaseTypeExchangeDefinitionTableWidget(this.caseTypeId);
                }
                break;
            case 'skeletons':
                if (!this.skeletonWidget) {
                    this.skeletonWidget = buildCwCaseTypeExchangeSkeletonTableWidget(this.caseTypeId);
                }
                break;
            case 'fieldDefinitions':
                if (!this.fieldDefinitionWidget) {
                    this.fieldDefinitionWidget = buildCwCaseTypeExchangeFieldDefinitionTableWidget(this.caseTypeId);
                }
                break;
            case 'responseMappings':
                if (!this.responseMappingWidget) {
                    this.responseMappingWidget = buildCwCaseTypeExchangeResponseMappingTableWidget(this.caseTypeId);
                }
                break;
            case 'echoRules':
                // #C.1: built only once the version binding is known, so the very first query is
                // already the union (own rules + inherited rules). See partyVersionResolved.
                if (!this.echoRuleWidget && this.partyVersionResolved) {
                    this.echoRuleWidget = buildCwCaseTypeExchangeEchoRuleTableWidget(
                        this.caseTypeId,
                        this.caseTypeExternalPartyVersionId ?? undefined
                    );
                    // #C.1: the table widget's „Anlegen" entry cannot be configured away, so it is
                    // PINNED to the case-type axis instead — a row created from a Vorgangstyp is a
                    // Vorgangstyp row, never a second copy on the version axis and never a scopeless
                    // row. Party-version rows are created on the party-version surface.
                    this.echoRuleWidget.functionCallbacks =
                        cwScopedCreateCallback('caseType', this.caseTypeId);
                }
                break;
        }
    }

    private loadCaseTypeBiproFlow(): void {
        const service = this.coreService.getCrudService('cw.CwCaseTypeBipro');
        if (!service) {
            return;
        }
        const request = new ObjectRequestList(
            false,
            [FilterCriteria.create('caseType', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            []
        );
        service.list(request).subscribe({
            next: (result: any) => {
                this.caseTypeBiproFlow = result?.entries?.[0]?.flow ?? CwBiproFlowEnum.SIMPLE;
            },
            error: () => {
                this.caseTypeBiproFlow = CwBiproFlowEnum.SIMPLE;
            }
        });
    }

    /**
     * #C.1 (cw_sales_enhancements_round_2) — resolves the cc.ExternalPartyVersion of this case type,
     * the axis whose rules the case type inherits. Best-effort exactly like
     * {@link loadCaseTypeBiproFlow}: an unresolvable binding leaves the id {@code null}, and the
     * Echo-Regeln tab then shows the case type's own rules only — never an error, never an empty tab.
     */
    private loadCaseTypeExternalPartyVersion(): void {
        this.partyVersionResolved = false;
        this.caseTypeExternalPartyVersionId = null;

        const service = this.coreService.getCrudService('cw.CwCaseType');
        if (!service) {
            this.partyVersionResolved = true;
            this.buildActiveTab();
            return;
        }

        service.get(this.caseTypeId).subscribe({
            next: (dto: any) => {
                this.caseTypeExternalPartyVersionId = dto?.externalPartyVersionDtoId ?? null;
                this.partyVersionResolved = true;
                this.buildActiveTab();
            },
            error: () => {
                this.partyVersionResolved = true;
                this.buildActiveTab();
            }
        });
    }

    /**
     * Opens Konfiguration → Partei-Version-Konfiguration WITH this case type's own version already
     * selected. Paired with the inheritance hint: naming a place the user then has to re-find, and
     * re-select in, is a hint that gets ignored. Takes the user OFF this page — deliberately, because
     * the row they want to change is not this case type's to change.
     */
    openPartyVersionConfig(): void {
        cwOpenPartyVersionConfig(this.router, this.caseTypeExternalPartyVersionId ?? undefined);
    }

    /** Whether the Echo-Regeln table can contain inherited rows at all (drives the hint panel). */
    hasInheritedScope(): boolean {
        return this.caseTypeExternalPartyVersionId != null;
    }

    private resetWidgets(): void {
        this.biproWidget = null;
        this.exchangeDefinitionWidget = null;
        this.skeletonWidget = null;
        this.fieldDefinitionWidget = null;
        this.responseMappingWidget = null;
        this.echoRuleWidget = null;
    }
}
